import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface EnrichmentDataDisplayProps {
  enrichmentData: any;
}

export const EnrichmentDataDisplay = ({ enrichmentData }: EnrichmentDataDisplayProps) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    llm_response: false,
    person_search: false,
    company_search: false,
    linkedin_search: false,
    extraction_prompt: false,
    extracted_data: true, // Default open
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderValue = (value: any, depth = 0): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">null</span>;
    }

    if (typeof value === 'boolean') {
      return <Badge variant={value ? 'default' : 'secondary'}>{value.toString()}</Badge>;
    }

    if (typeof value === 'number') {
      return <span className="text-primary font-medium">{value}</span>;
    }

    if (typeof value === 'string') {
      // Try to parse if it looks like JSON
      if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(value);
          return renderValue(parsed, depth);
        } catch {
          // Not JSON, render as string
        }
      }
      
      // If it's a long string, make it collapsible
      if (value.length > 200) {
        return (
          <div className="space-y-1">
            <p className="text-sm whitespace-pre-wrap break-words">{value.substring(0, 200)}...</p>
            <details className="text-xs">
              <summary className="cursor-pointer text-primary hover:underline">Show full text</summary>
              <p className="mt-2 text-sm whitespace-pre-wrap break-words">{value}</p>
            </details>
          </div>
        );
      }
      
      return <span className="text-sm whitespace-pre-wrap break-words">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-muted-foreground italic">empty array</span>;
      }
      
      return (
        <div className="space-y-2 pl-4 border-l-2 border-border">
          {value.map((item, idx) => (
            <div key={idx} className="space-y-1">
              <Badge variant="outline" className="text-xs">Item {idx + 1}</Badge>
              <div className="pl-2">{renderValue(item, depth + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className="text-muted-foreground italic">empty object</span>;
      }

      return (
        <div className="space-y-3 pl-4 border-l-2 border-border">
          {entries.map(([key, val]) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {key.replace(/_/g, ' ')}
              </Label>
              <div className="pl-2">{renderValue(val, depth + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  const sections = [
    { key: 'extracted_data', label: 'Extracted Data', icon: '📊' },
    { key: 'llm_response', label: 'AI Analysis Response', icon: '🤖' },
    { key: 'person_search', label: 'Person Search Query', icon: '👤' },
    { key: 'company_search', label: 'Company Search Query', icon: '🏢' },
    { key: 'linkedin_search', label: 'LinkedIn Search Query', icon: '💼' },
    { key: 'extraction_prompt', label: 'Extraction Prompt', icon: '📝' },
  ];

  return (
    <div className="space-y-4">
      {sections.map(section => {
        const value = enrichmentData[section.key];
        if (!value) return null;

        const isExpanded = expandedSections[section.key];

        return (
          <Collapsible
            key={section.key}
            open={isExpanded}
            onOpenChange={() => toggleSection(section.key)}
          >
            <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted hover:bg-muted/80 rounded-lg transition-colors">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-lg">{section.icon}</span>
              <span className="font-medium">{section.label}</span>
              {!isExpanded && (
                <Badge variant="secondary" className="ml-auto text-xs">
                  Click to expand
                </Badge>
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3 pl-8">
              <div className="p-4 bg-background rounded-md border">
                {renderValue(value)}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
};
